import { create } from "zustand";

/** Every join starts here (#1722). */
export const DEFAULT_CALL_VOLUME = 100;

/**
 * Session-scoped output volume for remote participants' voices (#1722).
 *
 * Lives outside InCallView so the level survives the full view ↔ floating
 * widget swap (InCallView unmounts when the widget shows). Deliberately NOT
 * persisted anywhere — `reset()` runs on every fresh join and on leave, so
 * each call starts at 100%.
 *
 * The speaker on/off toggle is tied to the slider: "off" is simply 0%, and
 * "on" restores `lastNonZero` — the last level before 0, whether 0 was
 * reached by toggling or by dragging the slider down.
 */
interface CallVolumeState {
  /** 0–100. 0 means the speaker is muted. */
  volume: number;
  /** Level restored when the speaker is turned back on. Always 1–100. */
  lastNonZero: number;
  /** Level captured when a slider drag began; null outside a drag. */
  adjustStart: number | null;
  setVolume: (pct: number) => void;
  toggleSpeakerOn: () => void;
  /** Slider drag started — intermediate values must not overwrite
   *  `lastNonZero` (dragging 60 → 0 passes through 1 on the way down). */
  beginAdjust: () => void;
  /** Slider drag ended — commit `lastNonZero` from the final position, or
   *  from the drag's starting level when the drag ended at 0. */
  endAdjust: () => void;
  reset: () => void;
}

function clampPct(pct: number): number {
  if (!Number.isFinite(pct)) return 0;
  return Math.min(100, Math.max(0, Math.round(pct)));
}

export const useCallVolumeStore = create<CallVolumeState>((set) => ({
  volume: DEFAULT_CALL_VOLUME,
  lastNonZero: DEFAULT_CALL_VOLUME,
  adjustStart: null,
  setVolume: (pct) =>
    set((s) => {
      const volume = clampPct(pct);
      if (s.adjustStart !== null || volume === 0) return { volume };
      return { volume, lastNonZero: volume };
    }),
  toggleSpeakerOn: () =>
    set((s) =>
      s.volume > 0
        ? { volume: 0, lastNonZero: s.volume, adjustStart: null }
        : { volume: s.lastNonZero, adjustStart: null },
    ),
  beginAdjust: () =>
    set((s) => (s.adjustStart === null ? { adjustStart: s.volume } : {})),
  endAdjust: () =>
    set((s) => {
      if (s.adjustStart === null) return {};
      if (s.volume > 0) return { lastNonZero: s.volume, adjustStart: null };
      return {
        lastNonZero: s.adjustStart > 0 ? s.adjustStart : s.lastNonZero,
        adjustStart: null,
      };
    }),
  reset: () =>
    set({
      volume: DEFAULT_CALL_VOLUME,
      lastNonZero: DEFAULT_CALL_VOLUME,
      adjustStart: null,
    }),
}));
